/**
 * One formula group over one metric bag — the Flow Optimizer's scoring
 * rules, lifted so the Hour Optimizer (Phase 16) ranks on the SAME math:
 * sub-formula substitution (blocks), the canonical expression re-derived
 * from the authoring body (a persisted expression can go stale), and the
 * per-metric / per-sub-formula contributions the hovers show.
 * For Media Devoted | Created By Ciro Autiero
 */
import type {
  FormulaGroup,
  FormulaSubformula,
} from "../../voluum-flow-optimizer.types";
import { makeSubformulaRefName } from "../../voluum-flow-optimizer.types";
import { evaluateFormula, type Metrics } from "../evaluator";
import { isLowestMaxKey, withLowestMax } from "../lowest/lowest";
import { serializeSliders } from "../serialize-sliders";
import { normalizeSubformulaWeights } from "../subformula-weights";
import {
  computeSliderContributions,
  computeSubformulaContributions,
  type SliderContribution,
  type SubformulaContribution,
} from "./score-group.contributions";

/** The part of a formula group the scorer reads (no layer scoping), plus
 *  the ranker's max of each lowest() metric (02/11/2026, runtime only). */
export type ScorableGroup = Pick<
  FormulaGroup,
  "mode" | "sliders" | "blocks" | "expression"
> & { lowestMax?: Record<string, number> };

export interface GroupScore {
  rawScore: number;
  /** The expression that really ran — the server-recomputed canonical form. */
  expression: string;
  contributions?: SliderContribution[];
  subformulaContributions?: SubformulaContribution[];
}

export function scoreGroup(group: ScorableGroup, bag: Metrics): GroupScore {
  const baseMetrics = withLowestMax(bag, group.lowestMax);
  const metrics = withSubformulaValues(baseMetrics, group);
  const expression = resolveEffectiveExpression(group);
  const rawScore = evaluateFormula(expression, metrics);
  const contributions = computeSliderContributions(group, baseMetrics);
  const subformulaContributions = computeSubformulaContributions(
    group,
    baseMetrics,
    evaluateSubformula,
  );
  return {
    rawScore,
    expression,
    ...(contributions ? { contributions } : {}),
    ...(subformulaContributions ? { subformulaContributions } : {}),
  };
}

/**
 * The persisted `expression` can go stale (sliders edited in an older app
 * version, or saved before a drag fired the recompute). Always re-derive the
 * canonical expression from the authoring body.
 */
export function resolveEffectiveExpression(group: ScorableGroup): string {
  if (group.mode === "sliders") {
    const derived = serializeSliders(group.sliders);
    if (derived && derived !== "0") return derived;
  }
  if (group.mode === "blocks") {
    const master = group.blocks?.masterExpression?.trim();
    if (master) return master;
  }
  return group.expression || "0";
}

/** Blocks mode: each sub-formula's value (scaled by its normalized weight
 *  once any weight is set) joins the bag under its `__sub_<id>` name. */
export function withSubformulaValues(
  metrics: Metrics,
  group: ScorableGroup,
): Metrics {
  if (group.mode !== "blocks") return metrics;
  const subs = group.blocks?.subformulas;
  if (!subs || subs.length === 0) return metrics;
  const weighted = subs.some((s) => typeof s.weight === "number")
    ? normalizeSubformulaWeights(subs)
    : null;
  const augmented: Metrics = { ...metrics };
  for (const sub of subs) {
    const value = evaluateSubformula(sub, metrics);
    augmented[makeSubformulaRefName(sub.id)] = weighted
      ? value * ((weighted[sub.id] ?? 0) / 100)
      : value;
  }
  return augmented;
}

export function evaluateSubformula(
  sub: FormulaSubformula,
  metrics: Metrics,
): number {
  const expr = (sub.expression ?? "").trim();
  if (!expr) return 0;
  try {
    return evaluateFormula(expr, metrics);
  } catch {
    return 0;
  }
}

/** Raw catalog metrics only — sub-formula refs and lowest() maxima are derived, not inputs. */
export function stripSubformulaKeys(metrics: Metrics): Metrics {
  const out: Metrics = {};
  for (const [k, v] of Object.entries(metrics)) {
    if (k.startsWith("__sub_") || isLowestMaxKey(k)) continue;
    out[k] = v;
  }
  return out;
}
